import { ColDef } from 'ag-grid-community';
import { DateRangeFilter } from '@shared/components/date-range-filter/date-range-filter';
import { TratamientoFilter } from './tratamiento-filter';

/**
 * Formats a bare calendar date ('YYYY-MM-DD') as 'DD/MM/YYYY' using pure string
 * manipulation. These columns are stored as DB `date` values (no time, no
 * timezone), so they must NOT be parsed through `new Date()` /
 * `toLocaleDateString()`: that interprets the string as UTC midnight and then
 * shifts it to the viewer's local day, showing the previous day in negative-UTC
 * timezones (e.g. Colombia, UTC-5). The selected date must read the same for
 * every user, so no timezone calculation is done here.
 */
const formatDateOnly = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

export const getColDefs = (): ColDef[] => {
  const colDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      hide: true,
    },
    {
      headerName: 'Documento',
      field: 'numero_documento',
      width: 150,
    },
    {
      headerName: 'Paciente',
      field: 'nombre_completo',
      width: 300,
    },
    {
      headerName: 'Tratamiento',
      field: 'tratamiento',
      width: 310,
      filter: TratamientoFilter,
    },
    {
      headerName: 'Departamento Tratamiento',
      field: 'departamento_tratamiento',
      width: 250,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        trimInput: true,
      },
    },
    {
      headerName: 'Ciudad Tratamiento',
      field: 'municipio_tratamiento',
      width: 250,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        trimInput: true,
      },
    },
    {
      headerName: 'Inicio TTO',
      headerTooltip: 'Fecha de inicio del tratamiento',
      field: 'fecha_inicio_tratamiento',
      width: 150,
      filter: DateRangeFilter,
      valueFormatter: params => formatDateOnly(params.value),
    },
    {
      headerName: 'Egreso',
      headerTooltip: 'Fecha de egreso',
      field: 'fecha_egreso',
      width: 150,
      filter: DateRangeFilter,
      valueFormatter: params => formatDateOnly(params.value),
    },
    {
      headerName: 'Condición Egreso',
      field: 'condicion_egreso',
      width: 220,
    },
    {
      headerName: 'Georreferenciación',
      field: 'georreferenciacion',
      width: 180,
      // Filtered via the "Pacientes con Georreferenciación" toggle, not a column
      // filter (defaultColDef would otherwise attach the text filter).
      filter: false,
      valueFormatter: params => (params.value ? 'Sí' : 'No'),
    },
    {
      headerName: 'IEC',
      headerTooltip: 'Investigación Epidemiológica de Campo',
      field: 'tiene_contactos',
      width: 100,
      // TB-only flag (NULL for LEPRA/QMO). Filtered via the "Investigación
      // Epidemiológica de Campo" toggle, not a column filter (defaultColDef
      // would otherwise attach the text filter).
      filter: false,
      valueFormatter: params => {
        // NULL means the flag does not apply (LEPRA/QMO are not TB) → blank.
        // TB rows are true/false → Sí/No.
        if (params.value === null || params.value === undefined) {
          return '';
        }
        return params.value ? 'Sí' : 'No';
      },
    },
    {
      headerName: 'Creación',
      headerTooltip: 'Fecha de creación del paciente',
      field: 'fecha_creacion',
      width: 180,
      filter: DateRangeFilter,
      valueFormatter: params => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString('es-CO');
        }
        return '';
      },
      sort: 'desc',
    },
  ];

  return colDefs;
};
